import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatDecimal,
  formatDotted,
  formatDuration,
  formatNumber,
  formatShortDate,
  parseDotted,
  type Workout,
  type Exercise,
} from '@jungles/shared';
import { api } from '../lib/api';
import { workoutHistoryTitle } from '../lib/workoutTitle';
import { useApi, useDebounced } from '../lib/hooks';
import { useUser } from '../app/session';
import { useToast } from '../app/toast';
import { BackLink, ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';
import { Dialog } from '../components/Sheet';

/** T6–T7 · История движения (SPEC §5). График — объём в кг, не очки. */

interface Summary {
  chart: { date: string; volumeKg: number }[];
}

export function HistoryScreen() {
  const user = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: exercises } = useApi<Exercise[]>('/api/exercises');

  const [query, setQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [onlyFavorite, setOnlyFavorite] = useState(false);
  const [toDelete, setToDelete] = useState<Workout | null>(null);
  const [busy, setBusy] = useState(false);

  const debouncedQuery = useDebounced(query);
  const year = Number(new Date().getFullYear());
  const parsedDate = dateFilter ? parseDotted(dateFilter, year) : null;

  const path = useMemo(() => {
    const params = new URLSearchParams({ status: 'done', limit: '100' });
    if (debouncedQuery.trim()) params.set('q', debouncedQuery.trim());
    if (parsedDate) params.set('date', parsedDate);
    if (onlyFavorite) params.set('favorite', 'true');
    return `/api/workouts?${params.toString()}`;
  }, [debouncedQuery, parsedDate, onlyFavorite]);

  const { data, error, loading, reload } = useApi<Workout[]>(path);
  const { data: summary } = useApi<Summary>('/api/workouts/summary');

  const chart = summary?.chart ?? [];
  const maxVolume = Math.max(1, ...chart.map((point) => point.volumeKg));

  const toggleFavorite = async (workout: Workout) => {
    try {
      await api.patch(`/api/workouts/${workout.id}`, { isFavorite: !workout.isFavorite });
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось изменить');
    }
  };

  const repeat = async (workout: Workout) => {
    try {
      const created = await api.post<Workout>('/api/workouts', {
        type: workout.type === 'activity' ? 'activity' : 'strength',
        copyFromWorkoutId: workout.id,
      });
      navigate(`/workouts/${created.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось повторить');
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    setBusy(true);
    try {
      await api.del(`/api/workouts/${toDelete.id}`);
      toast('Тренировка удалена, очки пересчитаны');
      setToDelete(null);
      reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen tabs>
      <Header />
      <BackLink to="/workouts" label="Тренировки" />
      <h1>История движения</h1>

      <section className="card">
        <h2>Найти запись</h2>
        <label className="fl">
          Поиск по названию или упражнению
          <input
            className="f"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Например, жим"
          />
        </label>
        <label className="fl">
          Дата <span className="mu">· пусто = все даты</span>
          <input
            className="f"
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
            placeholder="дд.мм.гггг"
            inputMode="numeric"
          />
        </label>
        <label className="fl">
          <input
            className="cb"
            type="checkbox"
            checked={onlyFavorite}
            onChange={(event) => setOnlyFavorite(event.target.checked)}
          />
          Только избранное
        </label>
        <button
          className="b wide"
          type="button"
          onClick={() => {
            setQuery('');
            setDateFilter('');
            setOnlyFavorite(false);
          }}
        >
          Сбросить фильтры
        </button>
      </section>

      {chart.length > 0 ? (
        <section className="card">
          <h2>
            {chart.length === 1 ? 'Последняя силовая' : `Последние ${chart.length} силовых`}
          </h2>
          <div className="bars" aria-label="Объём в килограммах">
            {chart.map((point) => (
              <div
                key={point.date}
                style={{ height: `${Math.max(12, (point.volumeKg / maxVolume) * 100)}%` }}
                title={`${formatShortDate(point.date)}: ${formatNumber(point.volumeKg)} кг`}
              >
                {formatNumber(point.volumeKg)}
              </div>
            ))}
          </div>
          <p className="nt">По времени записи слева направо · кг, не очки.</p>
        </section>
      ) : null}

      <section className="card">
        <h2>Записи</h2>
        {loading ? <Loader /> : null}
        {error ? <ErrorState message={error} onRetry={reload} /> : null}
        {!loading && (data ?? []).length === 0 ? (
          <p className="empty">Записей не нашлось</p>
        ) : null}

        {(data ?? []).map((workout) => (
          <article className="li" key={workout.id}>
            <div className="row">
              <h3>{workoutHistoryTitle(workout, exercises)}</h3>
              <button
                className="b"
                type="button"
                style={{ padding: '12px 16px', flex: 'none', width: 'auto' }}
                onClick={() => void toggleFavorite(workout)}
                aria-pressed={workout.isFavorite}
              >
                {workout.isFavorite ? '★' : '☆'} Избранное
              </button>
            </div>
            <p style={{ margin: '8px 0' }}>
              {formatDotted(workout.date)}
              {workout.durationMin ? ` · ${formatDuration(workout.durationMin)}` : ''}
              {workout.volumeKg > 0 ? ` · ${formatNumber(workout.volumeKg)} кг` : ''}
            </p>
            <small className="mu">
              {workout.exercises.length > 0
                ? workout.exercises
                    .slice(0, 3)
                    .map((exercise) => {
                      const best = exercise.sets.find((set) => set.weightKg !== null);
                      return best
                        ? `${exercise.name} ${best.weightKg} × ${best.reps ?? '—'}`
                        : exercise.name;
                    })
                    .join(' · ')
                : workout.distanceKm
                  ? `${formatDecimal(workout.distanceKm)} км`
                  : 'Без упражнений'}
            </small>
            <div className="acts">
              <button
                className="b"
                type="button"
                onClick={() => navigate(`/workouts/${workout.id}`)}
              >
                Изменить
              </button>
              <button className="b" type="button" onClick={() => void repeat(workout)}>
                Повторить
              </button>
              <button className="b d" type="button" onClick={() => setToDelete(workout)}>
                Удалить
              </button>
            </div>
          </article>
        ))}
      </section>

      <TabBar />

      {toDelete ? (
        <Dialog
          title="Удалить тренировку?"
          confirmLabel="Удалить"
          danger
          busy={busy}
          onCancel={() => setToDelete(null)}
          onConfirm={() => void remove()}
        >
          <p>Обновятся минуты, объём и игровые очки. Остальные записи останутся.</p>
          <p className="nt" style={{ margin: 0 }}>
            {workoutHistoryTitle(toDelete, exercises)} · {formatShortDate(toDelete.date)} ·{' '}
            {user.homeGymName ?? 'без зала'}
          </p>
        </Dialog>
      ) : null}
    </Screen>
  );
}
